import {
  overwatchFigureDefaults,
  overwatchMissionDefaults,
  overwatchPhaseDefaults,
  overwatchRulesDefaults,
  overwatchSceneDefaults,
  overwatchSpawnDefaults,
} from '../s3/defaults';
import {
  OW_OBJECTIVE_TYPES,
  OW_ROLES,
  ROUTINE_KINDS,
  TRAIT_AXES,
  type OverwatchDef,
  type OverwatchFigureDef,
  type OverwatchPlanDef,
  type OverwatchRulesDef,
  type OverwatchSceneDef,
  type ScopeDef,
} from '../s3/types';
import type { Err, Ids } from './common';
import { filled, fmtNum, rangeMsg } from './s2common';
import { CONDITION_IDS, type Bundle } from './types';

type At = (path: string, message: string) => void;

/** shared-cs `OverwatchRoster.MaxFigures`. */
export const OW_MAX_FIGURES = 30;

const n = (v: number | null | undefined) => (typeof v === 'number' ? v : 0);
const isLowerFirst = (s: string) => /^\p{Ll}/u.test(s);
/** `DescriptionRules.IsNone`. */
const isNone = (v: string | null | undefined) => !v || v === 'none' || v === 'nothing';

/** `OwPlan.TryZone`. */
const hasZone = (plan: OverwatchPlanDef, name: string | null | undefined) =>
  !!name && plan.zones != null && plan.zones[name] != null;
/** `OwPlan.Path(...).Count`: the non-null points of a named path. */
const pathCount = (plan: OverwatchPlanDef, name: string | null | undefined) =>
  !name || plan.paths == null ? 0 : (plan.paths[name] ?? []).filter((p) => p != null).length;
/** `OwPlan.TryPlace`: a zone, or a path with a point. */
const isPlace = (plan: OverwatchPlanDef, name: string | null | undefined) =>
  hasZone(plan, name) || pathCount(plan, name) > 0;

/**
 * Port of shared-cs `ContentValidator.Overwatch.cs` (`OverwatchSections`), S3-OWS: items' scopes, then the overwatch rules, trait pools,
 * figures, scenes (perches, arcs, plan), missions and phases (spawns, objectives, hidden timings, waves, the 30-figure cap), and the
 * enable checks. Same errors, same wording, same order. Absent blocks and fields are the C# initializers; an explicit `null` section is
 * "The overwatch section is missing.".
 */
export function validateOverwatch(b: Bundle, err: Err, warn: Err, ids: Ids): void {
  void warn;
  void ids;
  (b.items ?? []).forEach((item, i) => {
    if (item == null) return;
    const scope = item.weapon?.scope;
    if (scope != null)
      owScope(item, scope, (p, m) => err('items', i, `weapon.scope${p ? `.${p}` : ''}`, m));
  });

  if (b.overwatch === null) {
    err('overwatch', undefined, '', 'The overwatch section is missing.');
    return;
  }
  const ow = (b.overwatch ?? {}) as OverwatchDef;
  const at: At = (p, m) => err('overwatch', undefined, p, m);
  const range = (
    path: string,
    v: number,
    min: number,
    max: number,
    where: string,
    field: string,
  ) => {
    const m = rangeMsg(v, min, max, where, field);
    if (m) at(path, m);
  };
  const positive = (path: string, v: number, where: string, field: string) => {
    if (!(v > 0)) at(path, `${where} ${field} must be above 0.`);
  };
  const notNegative = (path: string, v: number, where: string, field: string) => {
    if (v < 0) at(path, `${where} ${field} is negative.`);
  };

  const r = filled(
    overwatchRulesDefaults(),
    (ow.rules ?? null) as OverwatchRulesDef | null,
  ) as OverwatchRulesDef & {
    perchChancePerSet?: number;
    crateRolls?: number[] | null;
    oneMoreShotCondition?: string;
  };
  const rw = 'Overwatch rules';
  range('rules.fillPerM', n(r.fillPerM), 0, 5, rw, 'fillPerM');
  range('rules.decayPerS', n(r.decayPerS), 0, 50, rw, 'decayPerS');
  range('rules.listenAt', n(r.listenAt), 0, 1, rw, 'listenAt');
  range('rules.relocateNoiseTo', n(r.relocateNoiseTo), 0, 1, rw, 'relocateNoiseTo');
  range('rules.relocateCostS', n(r.relocateCostS), 0, 120, rw, 'relocateCostS');
  if (n(r.maxRelocations) < 0 || n(r.maxRelocations) > 3)
    at('rules.maxRelocations', `${rw} needs maxRelocations in 0..3.`);
  range('rules.hitToleranceM', n(r.hitToleranceM), 0, 0.25, rw, 'hitToleranceM');
  range('rules.gravityMult', n(r.gravityMult), 0, 5, rw, 'gravityMult');
  const hz = n(r.simHz);
  if (hz < 5 || hz > 60 || 1000 % hz !== 0)
    at('rules.simHz', `${rw} needs simHz in 5..60 dividing 1000.`);
  if (n(r.aimTrackHz) < 1 || n(r.aimTrackHz) > 30)
    at('rules.aimTrackHz', `${rw} needs aimTrackHz in 1..30.`);
  range('rules.maxAimRadPerS', n(r.maxAimRadPerS), 0.5, 50, rw, 'maxAimRadPerS');
  range('rules.minVisibleS', n(r.minVisibleS), 0, 5, rw, 'minVisibleS');
  positive('rules.bodyRadiusM', n(r.bodyRadiusM), rw, 'bodyRadiusM');
  positive('rules.bodyHeightM', n(r.bodyHeightM), rw, 'bodyHeightM');
  positive('rules.headRadiusM', n(r.headRadiusM), rw, 'headRadiusM');
  positive('rules.headHeightM', n(r.headHeightM), rw, 'headHeightM');
  notNegative('rules.bystanderFleeM', n(r.bystanderFleeM), rw, 'bystanderFleeM');
  notNegative('rules.bystanderFleeS', n(r.bystanderFleeS), rw, 'bystanderFleeS');
  (r.perchTags ?? []).forEach((tag, i) => {
    if (!tag || tag.indexOf('=') <= 0)
      at(`rules.perchTags.${i}`, `${rw} perchTags '${tag ?? ''}' must be key=value.`);
  });
  if (n(r.minLevels) < 1) at('rules.minLevels', `${rw} needs minLevels of at least 1.`);
  positive('rules.minHeightM', n(r.minHeightM), rw, 'minHeightM');
  range('rules.perchCooldownH', n(r.perchCooldownH), 0, 168, rw, 'perchCooldownH');
  if (n(r.maxPerSet) < 0 || n(r.maxPerSet) > 1)
    at('rules.maxPerSet', `${rw} needs maxPerSet 0 or 1 (the specials cap allows one perch).`);
  if (n(r.importDensityMax) < 1)
    at('rules.importDensityMax', `${rw} needs importDensityMax of at least 1.`);
  positive('rules.importDensityRadiusM', n(r.importDensityRadiusM), rw, 'importDensityRadiusM');
  range('rules.energy', n(r.energy), 0, 100, rw, 'energy');
  range('rules.perchChancePerSet', n(r.perchChancePerSet ?? 1), 0, 1, rw, 'perchChancePerSet');
  const crate = r.crateRolls ?? [];
  if (crate.length !== 0 && crate.length !== 4)
    at('rules.crateRolls', `${rw} crateRolls needs 4 entries (C, B, A, S) or none.`);
  crate.forEach((c, i) => {
    if (c < 0 || c > 10) at(`rules.crateRolls.${i}`, `${rw} crateRolls must be in 0..10.`);
  });
  const oms = r.oneMoreShotCondition ?? 'wounded';
  if (!(CONDITION_IDS as readonly string[]).includes(oms))
    at('rules.oneMoreShotCondition', `${rw} oneMoreShotCondition '${oms}' isn't a condition.`);

  // Traits: the known axes, at least two values each, no duplicates, brown is "dark" (M36).
  const traits = (ow.traits ?? {}) as Record<string, string[] | null>;
  for (const [axis, raw] of Object.entries(traits)) {
    const where = `Overwatch trait axis '${axis}'`;
    if (!(TRAIT_AXES as readonly string[]).includes(axis))
      at(`traits.${axis}`, `${where} isn't one of ${TRAIT_AXES.join(', ')}.`);
    const values = raw ?? [];
    if (values.length < 2) at(`traits.${axis}`, `${where} needs at least two values.`);
    const seen = new Set<string>();
    values.forEach((v, i) => {
      if (!v || seen.has(v))
        at(`traits.${axis}.${i}`, `${where} has an empty or repeated value '${v ?? ''}'.`);
      if (v) seen.add(v);
      if (v === 'brown')
        at(
          `traits.${axis}.${i}`,
          `${where} uses 'brown': brown renders as dark ink, so the value is 'dark' (M36).`,
        );
    });
  }

  // Figures.
  const figures = new Map<string, OverwatchFigureDef>();
  const zombieIds = new Set((b.zombies ?? []).filter((z) => z != null).map((z) => z!.id ?? ''));
  (ow.figures ?? []).forEach((raw, i) => {
    if (raw == null) return;
    const f = filled(overwatchFigureDefaults(), raw);
    const fp = `figures.${i}`;
    const id = f.id ?? '';
    const where = `Overwatch figure '${id}'`;
    if (!id || figures.has(id)) at(`${fp}.id`, `Overwatch figures need unique ids ('${id}').`);
    else figures.set(id, f);
    if (f.kind !== 'human' && f.kind !== 'zombie')
      at(`${fp}.kind`, `${where} kind must be human or zombie.`);
    if (!(OW_ROLES as readonly string[]).includes(f.role ?? ''))
      at(`${fp}.role`, `${where} role must be one of ${OW_ROLES.join(', ')}.`);
    if (f.kind === 'zombie' && f.zombieId && !zombieIds.has(f.zombieId))
      at(`${fp}.zombieId`, `${where} has unknown zombieId '${f.zombieId}'.`);
    if (n(f.hp) <= 0 || n(f.hp) > 1000) at(`${fp}.hp`, `${where} needs hp in 1..1000.`);
    range(`${fp}.walkMps`, n(f.walkMps), 0.1, 10, where, 'walkMps');
    range(`${fp}.runMps`, n(f.runMps), 0.1, 12, where, 'runMps');
    if (f.reveal != null) {
      if (!f.reveal.kind) at(`${fp}.reveal.kind`, `${where} reveal needs a kind.`);
      range(`${fp}.reveal.everyS`, n(f.reveal.everyS), 0.5, 120, where, 'reveal.everyS');
    }
    if (f.threat != null) {
      range(`${fp}.threat.firstS`, n(f.threat.firstS), 0.5, 600, where, 'threat.firstS');
      range(`${fp}.threat.everyS`, n(f.threat.everyS), 0, 600, where, 'threat.everyS');
    }
  });

  // Scenes.
  const scenes = new Map<string, OverwatchSceneDef>();
  (ow.scenes ?? []).forEach((raw, i) => {
    if (raw == null) return;
    const s = filled(overwatchSceneDefaults(), raw);
    const sp = `scenes.${i}`;
    const id = s.id ?? '';
    const where = `Overwatch scene '${id}'`;
    if (!id || scenes.has(id)) at(`${sp}.id`, `Overwatch scenes need unique ids ('${id}').`);
    else scenes.set(id, s);
    if (!s.prefab) at(`${sp}.prefab`, `${where} needs a prefab.`);
    const perches = s.perches ?? [];
    if (perches.length === 0) at(`${sp}.perches`, `${where} needs at least one perch.`);
    const perchIds = new Set<string>();
    perches.forEach((p, j) => {
      if (p == null) return;
      const pp = `${sp}.perches.${j}`;
      const pid = p.id ?? '';
      const pw = `${where} perch '${pid}'`;
      if (!pid || perchIds.has(pid)) at(`${pp}.id`, `${where} needs unique perch ids ('${pid}').`);
      if (pid) perchIds.add(pid);
      const pos = p.pos ?? [];
      if (pos.length !== 3) at(`${pp}.pos`, `${pw} pos needs x, y, z.`);
      else if (!(pos[1]! > 0)) at(`${pp}.pos`, `${pw} needs a height (pos y) above 0.`);
      owArc(p.yawDeg, -180, 180, pw, 'yawDeg', (m) => at(`${pp}.yawDeg`, m));
      owArc(p.pitchDeg, -89, 89, pw, 'pitchDeg', (m) => at(`${pp}.pitchDeg`, m));
    });
    const plan = s.plan ?? {};
    for (const [zid, z] of Object.entries(plan.zones ?? {}))
      if (z == null || !(n(z.maxX) > n(z.minX)) || !(n(z.maxZ) > n(z.minZ)))
        at(`${sp}.plan.zones.${zid}`, `${where} zone '${zid}' needs min below max.`);
    for (const [pid, pts] of Object.entries(plan.paths ?? {}))
      if (pts == null || pts.length < 2)
        at(`${sp}.plan.paths.${pid}`, `${where} path '${pid}' needs at least two points.`);
    (plan.exits ?? []).forEach((e, j) => {
      if (!isPlace(plan, e))
        at(`${sp}.plan.exits.${j}`, `${where} exit '${e ?? ''}' isn't a zone or a path.`);
    });
    (plan.occluders ?? []).forEach((box, j) => {
      if (box?.min == null || box.max == null || box.min.length !== 3 || box.max.length !== 3)
        at(
          `${sp}.plan.occluders.${j}`,
          `${where} occluder '${box?.id ?? ''}' needs min and max as x, y, z.`,
        );
    });
  });

  // Missions.
  const scopedWeapons = new Set(
    (b.items ?? []).filter((it) => it?.weapon?.scope != null).map((it) => it!.id ?? ''),
  );
  const lootTables = new Set((b.lootTables ?? []).filter((t) => t != null).map((t) => t!.id ?? ''));
  const chains = new Set((b.beatChains ?? []).filter((c) => c != null).map((c) => c!.id ?? ''));
  const missions = new Set<string>();
  (ow.missions ?? []).forEach((raw, i) => {
    if (raw == null) return;
    const m = filled(overwatchMissionDefaults(), raw);
    const mp = `missions.${i}`;
    const id = m.id ?? '';
    const where = `Overwatch mission '${id}'`;
    if (!id || missions.has(id)) at(`${mp}.id`, `Overwatch missions need unique ids ('${id}').`);
    if (id) missions.add(id);
    const scene = scenes.get(m.sceneId ?? '');
    if (!scene) {
      at(`${mp}.sceneId`, `${where} has unknown scene '${m.sceneId ?? ''}'.`);
      return;
    }
    const plan = scene.plan ?? {};
    const onScene = (pid: string) => (scene.perches ?? []).some((p) => p != null && p.id === pid);
    if (!onScene(m.perchId ?? ''))
      at(
        `${mp}.perchId`,
        `${where} perch '${m.perchId ?? ''}' isn't on scene '${scene.id ?? ''}'.`,
      );
    if (m.altPerchId && !onScene(m.altPerchId))
      at(
        `${mp}.altPerchId`,
        `${where} altPerchId '${m.altPerchId}' isn't on scene '${scene.id ?? ''}'.`,
      );
    if (n(m.shotCap) < 1 || n(m.shotCap) > 30)
      at(`${mp}.shotCap`, `${where} needs shotCap in 1..30.`);
    (m.requiredWeaponIds ?? []).forEach((w, j) => {
      // S3-CON (M47): the scoped rifles that can play the mission; empty = any.
      if (!scopedWeapons.has(w ?? ''))
        at(
          `${mp}.requiredWeaponIds.${j}`,
          `${where} requires '${w ?? ''}', which isn't a scoped weapon.`,
        );
    });
    if (m.noKill !== 'soft' && m.noKill !== 'hard')
      at(`${mp}.noKill`, `${where} noKill must be soft or hard.`);
    if (m.rewardLootTable && !lootTables.has(m.rewardLootTable))
      at(`${mp}.rewardLootTable`, `${where} has unknown rewardLootTable '${m.rewardLootTable}'.`);
    if (m.briefingBeat && chains.size > 0 && !chains.has(m.briefingBeat))
      at(`${mp}.briefingBeat`, `${where} briefingBeat '${m.briefingBeat}' isn't a beat chain.`);
    const g = m.grades;
    if (g == null || g.length !== 4)
      at(`${mp}.grades`, `${where} needs 4 grade thresholds (C, B, A, S).`);
    else if (!(g[0]! <= g[1]! && g[1]! <= g[2]! && g[2]! <= g[3]!))
      at(`${mp}.grades`, `${where} grade thresholds must ascend.`);
    const phases = m.phases ?? [];
    if (phases.length === 0) {
      at(`${mp}.phases`, `${where} needs at least one phase.`);
      return;
    }
    const phaseIds = new Set<string>();
    let people = 0;
    let escortsSoFar = false;
    phases.forEach((rawPh, k) => {
      if (rawPh == null) return;
      const ph = filled(overwatchPhaseDefaults(), rawPh);
      const pp = `${mp}.phases.${k}`;
      const pid = ph.id ?? '';
      const pw = `${where} phase '${pid}'`;
      if (!pid || phaseIds.has(pid) || !isLowerFirst(pid) || pid.includes('.'))
        at(`${pp}.id`, `${where} needs unique lower-case phase ids without dots ('${pid}').`);
      if (pid) phaseIds.add(pid);
      range(`${pp}.timeS`, n(ph.timeS), 5, 600, pw, 'timeS');
      if (ph.revealBeat && chains.size > 0 && !chains.has(ph.revealBeat))
        at(`${pp}.revealBeat`, `${pw} revealBeat '${ph.revealBeat}' isn't a beat chain.`);
      let firstHuman = true;
      let firstHumanFigure: string | null = null;
      (ph.spawns ?? []).forEach((rawSp, s) => {
        if (rawSp == null) return;
        const sp = filled(overwatchSpawnDefaults(), rawSp);
        const spp = `${pp}.spawns.${s}`;
        const fig = sp.figure ?? '';
        const fd = figures.get(fig);
        if (!fd) at(`${spp}.figure`, `${pw} spawns unknown figure '${fig}'.`);
        if (n(sp.count) < 1 || n(sp.count) > OW_MAX_FIGURES)
          at(`${spp}.count`, `${pw} spawn '${fig}' needs count in 1..${OW_MAX_FIGURES}.`);
        people += Math.max(0, n(sp.count));
        if (sp.routine && !(ROUTINE_KINDS as readonly string[]).includes(sp.routine))
          at(`${spp}.routine`, `${pw} spawn routine '${sp.routine}' is unknown.`);
        if (sp.zone && !hasZone(plan, sp.zone))
          at(`${spp}.zone`, `${pw} spawn zone '${sp.zone}' isn't on the plan.`);
        if (sp.path && pathCount(plan, sp.path) < 2)
          at(`${spp}.path`, `${pw} spawn path '${sp.path}' isn't on the plan.`);
        if (!sp.zone && !sp.path) at(spp, `${pw} spawn '${fig}' needs a zone or a path.`);
        if (fd && fd.kind !== 'zombie') {
          if (fd.role === 'escort') escortsSoFar = true;
          if (firstHuman) firstHumanFigure = fd.id ?? '';
          firstHuman = false;
        }
      });

      const obj = { ...overwatchPhaseDefaults().objective, ...(ph.objective ?? {}) };
      const op = `${pp}.objective`;
      if (!(OW_OBJECTIVE_TYPES as readonly string[]).includes(obj.type ?? ''))
        at(`${op}.type`, `${pw} objective type '${obj.type ?? ''}' is unknown.`);
      if (obj.type === 'kill_target') {
        const describe = (obj.describe ?? []).length > 0;
        const modes = (describe ? 1 : 0) + (obj.hidden ? 1 : 0) + (!describe && obj.figure ? 1 : 0);
        if (modes !== 1)
          at(op, `${pw} kill_target needs exactly one of describe, hidden or figure.`);
        if (describe) {
          for (const axis of obj.describe ?? []) {
            if (!(TRAIT_AXES as readonly string[]).includes(axis))
              at(`${op}.describe`, `${pw} describes unknown trait axis '${axis}'.`);
            else if (!(axis in traits) || usable(traits[axis]) < 2)
              at(
                `${op}.describe`,
                `${pw} describes '${axis}', which needs two or more values that aren't none.`,
              );
          }
          if (n(obj.decoys) < 0 || n(obj.decoys) > 10)
            at(`${op}.decoys`, `${pw} needs decoys in 0..10.`);
          if (firstHumanFigure == null && !obj.figure)
            at(op, `${pw} describes a target but spawns no humans to dress.`);
          if (obj.figure && !figures.has(obj.figure))
            at(`${op}.figure`, `${pw} has unknown objective figure '${obj.figure}'.`);
          people += 1 + Math.max(0, n(obj.decoys));
          notNegative(`${op}.fleeOnMissWithinM`, n(obj.fleeOnMissWithinM), pw, 'fleeOnMissWithinM');
        } else if (
          obj.figure &&
          !(ph.spawns ?? []).some((s) => s != null && s.figure === obj.figure)
        ) {
          at(`${op}.figure`, `${pw} targets figure '${obj.figure}' but doesn't spawn it.`);
        }
      }
      if (obj.type === 'escort') {
        if (pathCount(plan, obj.path) < 2)
          at(`${op}.path`, `${pw} escort path '${obj.path ?? ''}' isn't on the plan.`);
        if (!escortsSoFar) at(op, `${pw} escorts, but no escort figure has spawned by then.`);
      }
      for (const [h, v] of Object.entries(ph.hidden ?? {})) {
        if (!h || !isLowerFirst(h) || !h.endsWith('S'))
          at(
            `${pp}.hidden.${h}`,
            `${pw} hidden '${h}' must be a lower-case time in seconds ending in S (turnAtS).`,
          );
        if (v == null || v.length !== 2 || v[0]! < 0 || v[1]! < v[0]! || v[1]! > n(ph.timeS))
          at(`${pp}.hidden.${h}`, `${pw} hidden '${h}' needs [min, max] inside the phase's time.`);
      }
      (ph.decoyTells ?? []).forEach((tell, t) => {
        if (!tell) at(`${pp}.decoyTells.${t}`, `${pw} has an empty decoy tell.`);
      });
      const waves = ph.waves;
      if (waves != null) {
        range(`${pp}.waves.everyS`, n(waves.everyS), 0, 120, pw, 'waves.everyS');
        if (n(waves.count) < 0 || n(waves.count) > 10)
          at(`${pp}.waves.count`, `${pw} needs waves.count in 0..10.`);
        (waves.from ?? []).forEach((from, f) => {
          if (!isPlace(plan, from))
            at(
              `${pp}.waves.from.${f}`,
              `${pw} waves come from '${from ?? ''}', which isn't a zone or a path.`,
            );
        });
        if ((waves.from ?? []).length === 0)
          at(`${pp}.waves.from`, `${pw} waves need at least one 'from' place.`);
        if (![...figures.values()].some((f) => f.kind === 'zombie'))
          at(`${pp}.waves`, `${pw} has waves but no zombie figure exists.`);
      }
    });
    if (people > OW_MAX_FIGURES)
      at(
        `${mp}.phases`,
        `${where} puts ${people} figures on the street; the most is ${OW_MAX_FIGURES}.`,
      );
  });

  if (r.enabled) {
    if (!r.perchMissionId || !missions.has(r.perchMissionId))
      at('rules.perchMissionId', `${rw} are enabled, so perchMissionId must name a mission.`);
    if (!r.perchTags || r.perchTags.length === 0)
      at(
        'rules.perchTags',
        `${rw} are enabled, so perchTags needs at least one tag (man_made=water_tower).`,
      );
  }
}

/** `OwUsable`: distinct values that aren't none. */
function usable(pool: string[] | null | undefined): number {
  return new Set((pool ?? []).filter((v) => !isNone(v))).size;
}

/** `OwArc`. */
function owArc(
  arc: number[] | null | undefined,
  min: number,
  max: number,
  where: string,
  field: string,
  add: (m: string) => void,
) {
  if (arc == null || arc.length !== 2) add(`${where} ${field} needs [min, max].`);
  else if (!(arc[0]! < arc[1]!) || arc[0]! < min || arc[1]! > max)
    add(`${where} ${field} needs min below max inside ${fmtNum(min)}..${fmtNum(max)}.`);
}

/** `OwScope`: a weapon's scope. */
function owScope(item: NonNullable<NonNullable<Bundle['items']>[number]>, s: ScopeDef, at: At) {
  const where = `Item '${item.id ?? ''}' scope`;
  const zooms = s.zooms ?? [];
  if (zooms.length === 0) at('zooms', `${where} needs at least one zoom level.`);
  let last = 1;
  for (const z of zooms) {
    if (!(z > last) || z > 16) at('zooms', `${where} zooms must ascend above 1x, up to 16x.`);
    last = z;
  }
  const range = (path: string, v: number, min: number, max: number, field: string) => {
    const m = rangeMsg(v, min, max, where, field);
    if (m) at(path, m);
  };
  range('muzzleMps', n(s.muzzleMps), 50, 2000, 'muzzleMps');
  range('maxRangeM', n(s.maxRangeM), 10, 1000, 'maxRangeM');
  if (!(n(s.zeroM) > 0) || n(s.zeroM) > n(s.maxRangeM))
    at('zeroM', `${where} needs zeroM above 0 and within maxRangeM.`);
  let bdc = 0;
  for (const d of s.bdcM ?? []) {
    if (!(d > bdc) || d > n(s.maxRangeM)) at('bdcM', `${where} bdcM must ascend within maxRangeM.`);
    bdc = d;
  }
  range('swayMrad', n(s.swayMrad), 0, 50, 'swayMrad');
  range('breathHoldS', s.breathHoldS ?? 4, 0, 30, 'breathHoldS');
  range('breathRefillS', s.breathRefillS ?? 5, 0, 60, 'breathRefillS');
  if (n(s.windMps) !== 0) at('windMps', `${where} windMps must be 0 in 0.60.0 (no wind yet).`);
  if (!item.weapon?.ammoItemId) at('', `${where} belongs to a weapon with no ammoItemId.`);
  if (!(n(item.weapon?.fireIntervalS) > 0))
    at('', `${where} belongs to a weapon with no fireIntervalS (the bolt).`);
}
